import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './MarkdownEditor.module.css';

const MarkdownEditor = ({
    content,
    onContentChange,
    components,
    isEditing,
    placeholder = 'Start writing...',
}) => {
    const [isFocused, setIsFocused] = useState(false);
    // eslint-disable-next-line
    const [isDragging, setIsDragging] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const textareaRef = useRef(null);

    // Markdown toolbar actions
    const insertMarkdown = (syntax, defaultText) => {
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = content.substring(start, end);

        const textToInsert = selectedText || defaultText;
        const newContent =
            content.substring(0, start) +
            syntax.replace('text', textToInsert) +
            content.substring(end);

        onContentChange(newContent);

        // Restore focus and selection
        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + syntax.indexOf('text');
            textarea.setSelectionRange(newCursorPos, newCursorPos + textToInsert.length);
        }, 0);
    };

    const toolbar = [
        {
            icon: '**',
            label: 'Bold',
            action: () => insertMarkdown('**text**', 'bold text')
        },
        {
            icon: '_',
            label: 'Italic',
            action: () => insertMarkdown('_text_', 'italic text')
        },
        {
            icon: '`',
            label: 'Code',
            action: () => insertMarkdown('`text`', 'code')
        },
        {
            icon: '#',
            label: 'Heading',
            action: () => insertMarkdown('\n# text\n', 'Heading')
        },
        {
            icon: '-',
            label: 'List',
            action: () => insertMarkdown('\n- text\n', 'List item')
        },
        {
            icon: '[',
            label: 'Link',
            action: () => insertMarkdown('[text](url)', 'link text')
        },
    ];

    const handleKeyDown = (e) => {
        // Handle keyboard shortcuts
        if (e.ctrlKey || e.metaKey) {
            switch (e.key) {
                case 'b':
                    e.preventDefault();
                    insertMarkdown('**text**', 'bold text');
                    break;
                case 'i':
                    e.preventDefault();
                    insertMarkdown('_text_', 'italic text');
                    break;
                case 'k':
                    e.preventDefault();
                    insertMarkdown('[text](url)', 'link text');
                    break;
                case 'f':
                    e.preventDefault();
                    setIsFullscreen(!isFullscreen);
                    break;
                default:
                    break;
            }
        }

        // Handle tab key
        if (e.key === 'Tab') {
            e.preventDefault();
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;

            // If text is selected, indent/unindent multiple lines
            if (start !== end) {
                const selectedText = content.substring(start, end);
                const lines = selectedText.split('\n');

                const newText = e.shiftKey
                    ? lines.map(line => line.startsWith('    ') ? line.slice(4) : line).join('\n')
                    : lines.map(line => '    ' + line).join('\n');

                const newContent = content.substring(0, start) + newText + content.substring(end);
                onContentChange(newContent);

                // Maintain selection
                setTimeout(() => {
                    e.target.selectionStart = start;
                    e.target.selectionEnd = start + newText.length;
                }, 0);
            } else {
                // Single line indentation
                const newContent = content.substring(0, start) + '    ' + content.substring(end);
                onContentChange(newContent);
                setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = start + 4;
                }, 0);
            }
        }
    };

    if (isEditing) {
        return (
            <div
                className={`${styles.editContainer} 
                    ${isDragging ? styles.dragging : ''} 
                    ${isFullscreen ? styles.fullscreen : ''}`}
            >
                <div className={styles.toolbar}>
                    {toolbar.map((item, index) => (
                        <button
                            key={index}
                            className={styles.toolbarButton}
                            onClick={item.action}
                            title={`${item.label} (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+${item.label[0]})`}
                        >
                            {item.icon}
                        </button>
                    ))}
                    <div className={styles.toolbarDivider} />
                    <button
                        className={`${styles.toolbarButton} ${isFullscreen ? styles.active : ''}`}
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        title="Toggle Fullscreen (Ctrl+F)"
                    >
                        ⛶
                    </button>
                </div>

                <textarea
                    ref={textareaRef}
                    className={`${styles.editor} ${isFocused ? styles.focused : ''}`}
                    value={content}
                    onChange={(e) => onContentChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    spellCheck="true"
                />

                <div className={styles.editorFooter}>
                    <div className={styles.stats}>
                        <span>{content.trim().split(/\s+/).filter(Boolean).length} words</span>
                        <span>{content.length} characters</span>
                        <span>{content.split('\n').length} lines</span>
                    </div>
                    <div className={styles.shortcuts}>
                        Ctrl+F: Full Screen | Ctrl+B: Bold | Ctrl+I: Italic | Ctrl+K: Link | Tab: Indent
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.viewContainer}>
            <ReactMarkdown
                components={{
                    ...components,
                    p: ({ node, children, ...props }) => <p className={styles.paragraph} {...props}>{children}</p>,
                    h1: ({ node, children, ...props }) => <h1 className={styles.heading1} {...props}>{children}</h1>,
                    h2: ({ node, children, ...props }) => <h2 className={styles.heading2} {...props}>{children}</h2>,
                    h3: ({ node, children, ...props }) => <h3 className={styles.heading3} {...props}>{children}</h3>,
                    ul: ({ node, children, ...props }) => <ul className={styles.list} {...props}>{children}</ul>,
                    ol: ({ node, children, ...props }) => <ol className={styles.orderedList} {...props}>{children}</ol>,
                    li: ({ node, children, ...props }) => <li className={styles.listItem} {...props}>{children}</li>,
                    code: ({ node, children, ...props }) => <code className={styles.code} {...props}>{children}</code>,
                    blockquote: ({ node, children, ...props }) => <blockquote className={styles.blockquote} {...props}>{children}</blockquote>,
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownEditor;